import React, { useEffect, useState } from "react";
import { listAll } from "../../services/firestore";
import { listTopProducts, listTopSearches } from "../../services/analytics";
import { BarChart3, Eye, Heart, Search, TrendingUp } from "lucide-react";

function Card({ title, icon: Icon, children }) {
  return (
    <div className="border border-[var(--border)] p-6">
      <div className="flex items-center gap-2 mb-4">
        <Icon size={16} className="text-[var(--primary)]" />
        <div className="metadata">{title}</div>
      </div>
      {children}
    </div>
  );
}

export default function AdminAnalytics() {
  const [products, setProducts] = useState([]);
  const [viewed, setViewed] = useState([]);
  const [favorited, setFavorited] = useState([]);
  const [searches, setSearches] = useState([]);
  const [orders, setOrders] = useState([]);
  const [users, setUsers] = useState([]);

  useEffect(() => {
    (async () => {
      const [p, v, f, s, o, u] = await Promise.all([
        listAll("products"),
        listTopProducts("views", 10),
        listTopProducts("favorites", 10),
        listTopSearches(10),
        listAll("orders"),
        listAll("users"),
      ]);
      setProducts(p);
      setViewed(v);
      setFavorited(f);
      setSearches(s);
      // Yalnız ödənilmiş sifarişləri analitikaya daxil edirik.
      setOrders(o.filter((x) => x.status !== "awaiting_payment" && x.status !== "payment_failed"));
      setUsers(u);
    })();
  }, []);

  const productById = Object.fromEntries(products.map((p) => [p.id, p]));
  const totalRevenue = orders.reduce((s, o) => s + (Number(o.total_azn) || 0), 0);
  const pending = orders.filter((o) => o.status !== "delivered").length;

  // Top selling (count items sold across orders)
  const sellCount = {};
  for (const o of orders) {
    for (const it of o.items || []) {
      sellCount[it.product_id] = (sellCount[it.product_id] || 0) + (it.qty || 0);
    }
  }
  const topSelling = Object.entries(sellCount).map(([pid, qty]) => ({ pid, qty })).sort((a, b) => b.qty - a.qty).slice(0, 10);

  return (
    <div className="p-10" data-testid="admin-analytics">
      <div className="metadata">STATİSTİKA</div>
      <h1 className="font-display text-5xl mt-1">Analitika</h1>

      {/* KPIs */}
      <div className="grid md:grid-cols-4 gap-4 mt-8">
        <div className="border border-[var(--primary)] p-6"><BarChart3 size={16} className="text-[var(--primary)]" /><div className="font-display text-3xl mt-3">₼{totalRevenue.toFixed(0)}</div><div className="metadata mt-1 text-[var(--text-2)]">ÜMUMİ GƏLİR</div></div>
        <div className="border border-[var(--border)] p-6"><TrendingUp size={16} className="text-[var(--primary)]" /><div className="font-display text-3xl mt-3">{orders.length}</div><div className="metadata mt-1 text-[var(--text-2)]">SİFARİŞ</div></div>
        <div className="border border-[var(--border)] p-6"><Eye size={16} className="text-[var(--primary)]" /><div className="font-display text-3xl mt-3">{pending}</div><div className="metadata mt-1 text-[var(--text-2)]">GÖZLƏMƏDƏ</div></div>
        <div className="border border-[var(--border)] p-6"><Heart size={16} className="text-[var(--primary)]" /><div className="font-display text-3xl mt-3">{users.length}</div><div className="metadata mt-1 text-[var(--text-2)]">MÜŞTƏRİ</div></div>
      </div>

      <div className="grid md:grid-cols-2 gap-4 mt-8">
        <Card title="ƏN ÇOX BAXILAN MƏHSULLAR" icon={Eye}>
          {viewed.length === 0 ? <div className="metadata text-[var(--text-muted)]">Hələ baxış yoxdur</div> :
            <ol className="space-y-2">
              {viewed.map((v, i) => {
                const p = productById[v.id];
                return (
                  <li key={v.id} className="flex items-center gap-3 py-1">
                    <span className="font-mono text-xs w-6 text-[var(--text-muted)]">{String(i + 1).padStart(2, "0")}</span>
                    {p?.cover_url ? <img src={p.cover_url} alt="" className="w-9 h-9 object-cover" /> : <div className="w-9 h-9 bg-[var(--surface)]" />}
                    <div className="flex-1 font-display">{p?.title || v.id}</div>
                    <span className="font-mono text-sm">{v.views || 0}</span>
                  </li>
                );
              })}
            </ol>}
        </Card>

        <Card title="ƏN ÇOX SEÇİLMİŞLƏRƏ ƏLAVƏ EDİLMİŞ" icon={Heart}>
          {favorited.length === 0 ? <div className="metadata text-[var(--text-muted)]">Hələ seçilmiş yoxdur</div> :
            <ol className="space-y-2">
              {favorited.map((v, i) => {
                const p = productById[v.id];
                return (
                  <li key={v.id} className="flex items-center gap-3 py-1">
                    <span className="font-mono text-xs w-6 text-[var(--text-muted)]">{String(i + 1).padStart(2, "0")}</span>
                    {p?.cover_url ? <img src={p.cover_url} alt="" className="w-9 h-9 object-cover" /> : <div className="w-9 h-9 bg-[var(--surface)]" />}
                    <div className="flex-1 font-display">{p?.title || v.id}</div>
                    <span className="font-mono text-sm">{v.favorites || 0}</span>
                  </li>
                );
              })}
            </ol>}
        </Card>

        <Card title="ƏN ÇOX AXTARILAN" icon={Search}>
          {searches.length === 0 ? <div className="metadata text-[var(--text-muted)]">Hələ axtarış yoxdur</div> :
            <ol className="space-y-2">
              {searches.map((s, i) => (
                <li key={s.query} className="flex items-center gap-3 py-1">
                  <span className="font-mono text-xs w-6 text-[var(--text-muted)]">{String(i + 1).padStart(2, "0")}</span>
                  <div className="flex-1 font-display">"{s.query}"</div>
                  <span className="font-mono text-sm">{s.count}×</span>
                </li>
              ))}
            </ol>}
        </Card>

        <Card title="ƏN ÇOX SATILAN" icon={TrendingUp}>
          {topSelling.length === 0 ? <div className="metadata text-[var(--text-muted)]">Hələ satış yoxdur</div> :
            <ol className="space-y-2">
              {topSelling.map((v, i) => {
                const p = productById[v.pid];
                return (
                  <li key={v.pid} className="flex items-center gap-3 py-1">
                    <span className="font-mono text-xs w-6 text-[var(--text-muted)]">{String(i + 1).padStart(2, "0")}</span>
                    {p?.cover_url ? <img src={p.cover_url} alt="" className="w-9 h-9 object-cover" /> : <div className="w-9 h-9 bg-[var(--surface)]" />}
                    <div className="flex-1 font-display">{p?.title || v.pid}</div>
                    <span className="font-mono text-sm">{v.qty}×</span>
                  </li>
                );
              })}
            </ol>}
        </Card>
      </div>
    </div>
  );
}
